/**
 * Route paths as constants, so no client ever writes a URL as a string. A
 * typo in a path is otherwise a 404 discovered at runtime; here it does not
 * compile.
 */
export const AUTH_ROUTES = {
    requestOtp: "/auth/otp/request",
    verifyOtp: "/auth/otp/verify",
    refresh: "/auth/refresh",
    signout: "/auth/signout",
    me: "/auth/me",
    /**
     * `POST /auth/ws-ticket` (`D34`) — the authenticated half of the WebSocket
     * ticket exchange. Answers `WsTicketOutput`.
     *
     * It lives beside the other five because it is an ORDINARY authenticated
     * call: an access token in the header it always travels in, and a short-lived
     * opaque ticket back. Only the ticket is unusual, and only because a
     * WebSocket handshake cannot carry a header.
     *
     * `apps/api/src/routes/ws-ticket.ts` declared this path privately while
     * `@repo/contract` was owned by another task in flight, and said so at the
     * declaration. It is here now because a second reader exists — the mobile
     * chat screen — and a path written twice is a 404 discovered at runtime.
     */
    wsTicket: "/auth/ws-ticket",
    /**
     * `PUT /auth/me/locale` (`D93`, which amends `D42`) — the person tells the
     * server which language to write to them in. Answers `204`, empty.
     *
     * ── WHY IT EXISTS WHEN THE LOCALE ALREADY RIDES TWO REQUESTS ─────────────
     * `requestOtpInput.locale` and `wsTicketIdentity.locale` both carry it, and
     * both keep carrying it — a message provoked by a request can read the
     * language off that request. This route is for the OTHER kind: a push the
     * server decided to send to a device that is making no request at all, with
     * nothing to carry a locale on. `D42` named that case and deferred it; this
     * is it arriving.
     *
     * ── `/auth/me/...`, BECAUSE THE SUBJECT IS THE CALLER AND NEVER AN ID ────
     * It is a child of `me` for the reason `me` has no id in it either: the
     * account is the one the verified token names, and the path can no more
     * address somebody else's language than `GET /auth/me` can read their
     * account. A `/auth/accounts/:id/locale` would be the same feature with an
     * authorization decision added to it.
     *
     * ── `PUT`, NOT `POST` OR `PATCH` ─────────────────────────────────────────
     * The resource is one value with no sub-structure, so a full replacement is
     * the whole of the write and `PATCH`'s partial semantics would describe
     * nothing. It is also idempotent, which matters here rather than being a
     * nicety: the app fires this and forgets it, so a retry after a lost
     * response has to be free. The five `POST`s above are each non-idempotent —
     * they mint, rotate or spend a credential — so this is a departure from
     * their shape only where their shape was about that.
     */
    setLocale: "/auth/me/locale",
    /**
     * `PUT /auth/me/display-name` (`D40`) — what the app calls this person.
     * Body `setDisplayNameInput`; `null` clears it. Answers `204`, empty.
     *
     * A child of `me`, with no id in the path, and a `PUT`, for every reason
     * `setLocale` gives above: the subject is the caller, the resource is one
     * value, and the write is idempotent so a retry after a lost response is
     * free.
     */
    setDisplayName: "/auth/me/display-name",
    /**
     * `PUT /auth/me/time-zone` (`D87`) — where this person lives, as an IANA
     * zone name. Body `setTimeZoneInput`. Answers `204`, empty.
     *
     * ── WHY THE SERVER NEEDS IT ──────────────────────────────────────────────
     * Two things are computed while the phone is asking for nothing: the week a
     * streak is counted in, and the hour a reminder fires. Both are UTC today,
     * which `D152` records as a known limitation precisely because the training
     * module holds no zone. `D87` said where one belongs — on the person — and
     * this is the route that puts it there.
     *
     * A child of `me`, with no id in the path, and a `PUT`, for every reason
     * `setLocale` gives above: the subject is the caller, the resource is one
     * value, and the write is idempotent — which matters here rather than being
     * a nicety, because the app fires this on resume and forgets it.
     */
    setTimeZone: "/auth/me/time-zone",
    /**
     * `PUT /auth/me/weight-unit` (`D266`) — `kg` or `lb`, the unit this person
     * reads and types loads in. Body `setWeightUnitInput`. Answers `204`.
     * A child of `me` and a `PUT` for every reason `setTimeZone` gives.
     */
    setWeightUnit: "/auth/me/weight-unit",
    /**
     * `DELETE /auth/account` (`D153`) — the person closes their own account.
     * Answers `204`, empty.
     *
     * ── WHAT IT DOES, IN THE ORDER IT DOES IT ────────────────────────────────
     * First the walk `D78` promised: every conversation the person holds with
     * the trainer (`subject_account IS NULL`) is erased in its Durable Object
     * and soft-deleted in the index. Then `DeleteAccount` marks the account and
     * revokes every credential (`D53`, `D63`). The walk runs FIRST and its
     * failure fails the request, so a half-deleted state is a `5xx` the person
     * sees and can retry, never a closed account whose transcripts survived.
     *
     * ── WHAT IT DOES NOT DO, AND THE COPY MAY NOT CLAIM ─────────────────────
     * Training rows stay (`D53`). A trainer's conversations ABOUT the person
     * stay (`D79`). The app's confirmation says exactly that.
     *
     * ── `/auth/account`, NOT `/auth/me` ─────────────────────────────────────
     * `GET /auth/me` answers "who am I"; a `DELETE` on the same path would read
     * as ending the session, which `POST /auth/signout` already is. The subject
     * is still the caller and nobody else — there is no id in the path, for
     * the reason `setLocale` gives — and the deletion command carries no actor
     * for the same reason `Signout` carries none.
     *
     * ── THE REFUSAL ──────────────────────────────────────────────────────────
     * `409` with `account.owns_populated_groups` while the person still owns a
     * group other active people are in, carrying
     * `params: { groupCount, groupNames, memberCount }` so the app can name
     * what to transfer or close (`D27`, `D53`). Nothing is erased on that path.
     */
    deleteAccount: "/auth/account",
};
//# sourceMappingURL=routes.js.map